import { EyeOffIcon } from '@persona-id/design-tokens/icons';
import { memo, ReactNode } from 'react';
import styled from 'styled-components';

import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { Elevation } from 'design-system/variables/effect';

export const COMPLETE_STEP_NODE_SIZE = 162;
const LOGO_SIZE = 46;
export const DEFAULT_DESCRIPTION = 'Exit Persona inquiry flow (no screen)';

const Node = styled(Button).attrs(() => ({ type: 'button' }))<{
  selected?: boolean;
  hasError?: boolean;
}>`
  display: grid;
  place-items: center;
  cursor: pointer;
  pointer-events: auto;
  user-select: none;
  width: ${COMPLETE_STEP_NODE_SIZE}px;
  height: ${COMPLETE_STEP_NODE_SIZE}px;
  box-shadow: ${Elevation.Mid};
  box-sizing: border-box;
  border: 2px solid;
  border-radius: ${({ theme }) => theme.card.borderRadius};
  z-index: ${Z_INDICES.withShadow};
  background-color: ${({ theme }) => theme.color.background.primary};
  border-color: ${({ theme, selected, hasError }) => {
    if (hasError) {
      return theme.color.variants.danger.highlight;
    } else if (selected) {
      return theme.color.primary.brand600;
    } else {
      return theme.color.variants.primary.highlight;
    }
  }};
  svg {
    width: ${LOGO_SIZE}px;
    height: ${LOGO_SIZE}px;
    color: ${({ theme }) => theme.color.primary.brand600};
  }
`;

export interface CompleteStepNodeProps {
  selected?: boolean;
  hasError?: boolean;
  nodeHighlighter?: ReactNode;
}

/**
 * This component is responsible for rendering a CompleteStepNode
 * representing a non-UI terminal complete step, which exits the
 * Persona Inquiry flow without displaying a terminal Persona screen.
 */
const CompleteStepNode = ({ selected, hasError, nodeHighlighter }: CompleteStepNodeProps) => {
  return (
    <Tooltip
      side='top'
      trigger={
        <Node selected={selected} hasError={hasError}>
          {nodeHighlighter}
          <EyeOffIcon size={24} />
        </Node>
      }
    >
      <Paragraph>{DEFAULT_DESCRIPTION}</Paragraph>
    </Tooltip>
  );
};

export default memo(CompleteStepNode);
